/**
 * SQL Minify Tool
 *
 * Minify SQL by collapsing whitespace while preserving string literals.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

export const SqlMinifyTool: ITransformerTool = {
  id: 'sql-minify',
  name: 'SQL Minify',
  description: 'Minify SQL by collapsing whitespace and removing comments',
  version: '1.0.0',
  type: 'transformer',
  category: 'Formatters & Minifiers',
  keywords: ['sql', 'minify', 'compress', 'compact', 'collapse'],

  seo: {
    slug: 'sql-minifier',
    pageTitle: 'SQL Minifier - Free Online Tool | SWE DevTools',
    metaDescription: 'Minify SQL queries by collapsing whitespace and removing comments. Reduce SQL size instantly. Fast, private, runs entirely in your browser.',
    keywords: ['sql minifier', 'minify sql online', 'sql compress', 'sql compact', 'collapse sql whitespace'],
    relatedTools: ['sql-format'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'removeComments',
      type: 'boolean',
      label: 'Remove Comments',
      defaultValue: true,
      description: 'Strip SQL comments (-- and /* */ style)',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const removeComments = config.removeComments !== false;
    const text = String(input ?? '').trim();
    if (!text) return '';

    try {
      // Tokenize to preserve string literals
      const tokens: string[] = [];
      let i = 0;

      while (i < text.length) {
        // Single-quoted string
        if (text[i] === "'") {
          let end = i + 1;
          while (end < text.length) {
            if (text[end] === "'" && text[end + 1] === "'") {
              end += 2; // escaped quote
            } else if (text[end] === "'") {
              end++;
              break;
            } else {
              end++;
            }
          }
          tokens.push(text.slice(i, end));
          i = end;
        }
        // Double-quoted identifier
        else if (text[i] === '"') {
          let end = i + 1;
          while (end < text.length && text[end] !== '"') end++;
          tokens.push(text.slice(i, end + 1));
          i = end + 1;
        }
        // Single-line comment
        else if (text[i] === '-' && text[i + 1] === '-') {
          const end = text.indexOf('\n', i);
          if (removeComments) {
            tokens.push(' ');
          } else {
            tokens.push(text.slice(i, end === -1 ? text.length : end));
          }
          i = end === -1 ? text.length : end + 1;
        }
        // Block comment
        else if (text[i] === '/' && text[i + 1] === '*') {
          const end = text.indexOf('*/', i + 2);
          if (removeComments) {
            tokens.push(' ');
          } else {
            tokens.push(text.slice(i, end === -1 ? text.length : end + 2));
          }
          i = end === -1 ? text.length : end + 2;
        }
        // Whitespace
        else if (/\s/.test(text[i])) {
          let end = i;
          while (end < text.length && /\s/.test(text[end])) end++;
          tokens.push(' ');
          i = end;
        }
        // Other characters
        else {
          let end = i;
          while (
            end < text.length &&
            !/\s/.test(text[end]) &&
            text[end] !== "'" &&
            text[end] !== '"' &&
            !(text[end] === '-' && text[end + 1] === '-') &&
            !(text[end] === '/' && text[end + 1] === '*')
          ) {
            end++;
          }
          tokens.push(text.slice(i, end));
          i = end;
        }
      }

      return tokens.join('').trim();
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to minify SQL';
      throw ToolError.execution(message);
    }
  },
};
